import { validatePassword, DEFAULT_PASSWORD_REQUIREMENTS } from '@/lib/passwordValidation';
import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

export function PasswordStrengthMeter({ password, className }: PasswordStrengthMeterProps) {
  const result = validatePassword(password);
  
  const strengthColors = {
    weak: 'bg-destructive',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500',
  };

  const strengthLabels = {
    weak: 'Fraca',
    medium: 'Média',
    strong: 'Forte',
  };

  const strengthWidth = {
    weak: '33%',
    medium: '66%',
    strong: '100%',
  };

  if (!password) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Força da senha:</span>
          <span className={cn(
            'font-medium',
            result.strength === 'weak' && 'text-destructive',
            result.strength === 'medium' && 'text-yellow-600',
            result.strength === 'strong' && 'text-green-600'
          )}>
            {strengthLabels[result.strength]}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', strengthColors[result.strength])}
            style={{ width: strengthWidth[result.strength] }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1 text-xs">
        <RequirementItem
          met={password.length >= DEFAULT_PASSWORD_REQUIREMENTS.minLength}
          text={`Mínimo ${DEFAULT_PASSWORD_REQUIREMENTS.minLength} caracteres`}
        />
        <RequirementItem
          met={/[A-Z]/.test(password)}
          text="Uma letra maiúscula"
        />
        <RequirementItem
          met={/[a-z]/.test(password)}
          text="Uma letra minúscula"
        />
        <RequirementItem
          met={/[0-9]/.test(password)}
          text="Um número"
        />
        <RequirementItem
          met={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)}
          text="Caractere especial (opcional)"
          optional
        />
      </div>
    </div>
  );
}

function RequirementItem({ met, text, optional = false }: { met: boolean; text: string; optional?: boolean }) {
  return (
    <div className={cn(
      'flex items-center gap-1.5',
      met ? 'text-green-600' : optional ? 'text-muted-foreground' : 'text-muted-foreground'
    )}>
      {met ? (
        <Check className="h-3 w-3" />
      ) : (
        <X className={cn('h-3 w-3', optional && 'opacity-50')} />
      )}
      <span className={optional && !met ? 'opacity-70' : ''}>{text}</span>
    </div>
  );
}
